import { useMemo } from 'react';

import type { AxiosInstance } from 'axios';
import axios from 'axios';

import { getAppInsights } from 'analytics/appInsights';
import { getAccessToken } from 'api/authentication/utils/getAccessToken';
import type { OutputOperationResult } from 'api/types';
import { ENV_VARS } from 'app/config/environment-vars';
import { HTTP_STORE } from 'state/http/httpStore';
import type {
  Condition,
  DataQuery,
  DisplayName,
  EAggregatingValueSourceType,
  EPropertyTypes,
  HyperRelation,
  IHaveId,
  IReportOption,
  ITraverseNode,
  ITraverseProperty,
  ITraverseRelation,
  Module,
  Node_,
  ProjectView,
  ProjectViewHeader,
  Property,
  Relation,
  ReturnNode,
  SingleValueQuery,
  Template,
  ValueSourceNode
} from 'state/selectedTemplate/selectedTemplateStore';
import type { TemplateSummary } from 'state/templates/templateState';

import type { CountResult, IDataEventBase } from '../data/client-dataapi';
import type { ImpactBrokerResult } from './impactBrokerResultModel';
import type { GenericNodeWithTechnicalName } from './templateModel';

// this hook can only be run inside the AppWithInitializedServices component
export const useMetaApiClient = () => {
  return useMemo(() => new MetaApiClient(), []);
};

export class MetaApiClient {
  private secureUrl!: string;

  private client!: AxiosInstance;

  constructor() {
    this.secureUrl = `${ENV_VARS.META_URL}/api/template`;
    this.client = axios.create({
      baseURL: this.secureUrl,

      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });
    this.client.interceptors.request.use((config) => {
      HTTP_STORE.getState().start('meta');
      const token = getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.client.interceptors.response.use(
      function (response) {
        // Do something with response data
        HTTP_STORE.getState().stop('meta');

        return response;
      },
      function (error) {
        const appInsights = getAppInsights();
        appInsights.trackException({ exception: new Error(`MetaApiClient ::: ${error.message}`) });

        HTTP_STORE.getState().error('meta', error);

        return Promise.reject(error);
      }
    );
  }

  public GetTemplates = async (): Promise<OutputOperationResult<TemplateSummary[]>> => {
    return await this.client.get('').then((response) => {
      return response.data as OutputOperationResult<TemplateSummary[]>;
    });
  };

  public CreateTemplate = async (
    model: CreateTemplateModel
  ): Promise<OutputOperationResult<GenericNodeWithTechnicalName>> => {
    return await this.client.post('', model).then((response) => {
      return response.data as OutputOperationResult<GenericNodeWithTechnicalName>;
    });
  };

  public CreateTemplateBranch = async (
    templateId: string,
    templateName: string
  ): Promise<OutputOperationResult<Template>> => {
    return await this.client
      .patch(`/${templateId}/:branch`, { branchTechnicalName: templateName } as CreateTemplateBranchModel)
      .then((response) => {
        return response.data as OutputOperationResult<Template>;
      });
  };

  public CreateTemplateVersion = async (
    templateId: string,
    templateName: string
  ): Promise<OutputOperationResult<Template>> => {
    return await this.client
      .patch(`/${templateId}/:version`, { branchTechnicalName: templateName } as CreateTemplateBranchModel)
      .then((response) => {
        return response.data as OutputOperationResult<Template>;
      });
  };

  public CreateTemplateNode = async (
    templateId: string,
    model: CreateNodeModel
  ): Promise<OutputOperationResult<Node_>> => {
    return await this.client.post(`/${templateId}/node`, model).then((response) => {
      return response.data as OutputOperationResult<Node_>;
    });
  };

  public MakeDecomposable = async (templateId: string, nodeId: string): Promise<OutputOperationResult<Relation>> => {
    return await this.client.patch(`/${templateId}/node/${nodeId}:decomposable`).then((response) => {
      return response.data as OutputOperationResult<Relation>;
    });
  };

  public DeleteNode = async (
    templateId: string,
    nodeId: string
  ): Promise<OutputOperationResult<GenericNodeWithTechnicalName>> => {
    return await this.client.delete(`/${templateId}/node/${nodeId}`).then((response) => {
      return response.data as OutputOperationResult<GenericNodeWithTechnicalName>;
    });
  };

  public UpdateNodePosition = async (
    templateId: string,
    nodeId: string,
    model: UpdateNodeCoordinatesModel
  ): Promise<OutputOperationResult<GenericNodeWithTechnicalName>> => {
    return await this.client.patch(`/${templateId}/node/${nodeId}/coordinates`, model).then((response) => {
      return response.data as OutputOperationResult<GenericNodeWithTechnicalName>;
    });
  };

  public UpdateNodeName = async (
    templateId: string,
    nodeId: string,
    model: UpdateNodeTechnicalNameModel
  ): Promise<OutputOperationResult<UpdateNodeNameResult>> => {
    return await this.client.patch(`/${templateId}/node/${nodeId}`, model).then((response) => {
      return response.data as OutputOperationResult<UpdateNodeNameResult>;
    });
  };

  public UpdateTemplateName = async (
    templateId: string,
    newName: string
  ): Promise<OutputOperationResult<GenericNodeWithTechnicalName>> => {
    return await this.client.patch(`/${templateId}`, { newName } as UpdateTemplateName).then((response) => {
      return response.data as OutputOperationResult<GenericNodeWithTechnicalName>;
    });
  };

  public CreateRelation = async (
    templateId: string,
    model: CreateTemplateRelationModel
  ): Promise<OutputOperationResult<TemplateRelationNode>> => {
    return await this.client.post(`/${templateId}/relation/`, model).then((response) => {
      return response.data as OutputOperationResult<TemplateRelationNode>;
    });
    //api/template/{templateId:guid}/relation
  };

  public ConvertRelationToHyperRelationValidateImpact = async (
    templateId: string,
    relationId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client
      .post(`/${templateId}/relation/${relationId}:to-hyper-relation/validateImpact`)
      .then((response) => {
        return response.data as OutputOperationResult<ImpactBrokerResult>;
      });
  };

  public ConvertRelationToHyperRelationApplyImpact = async (
    templateId: string,
    relationId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client
      .post(`/${templateId}/relation/${relationId}:to-hyper-relation/applyImpact`)
      .then((response) => {
        return response.data as OutputOperationResult<ImpactBrokerResult>;
      });
  };

  public DeleteRelation = async (
    templateId: string,
    relationId: string
  ): Promise<OutputOperationResult<TemplateRelationNode>> => {
    return await this.client.post(`/${templateId}/relation/${relationId}:delete`).then((response) => {
      return response.data as OutputOperationResult<TemplateRelationNode>;
    });
    //api/template/{templateId:guid}/relation
  };

  public DeleteRelationValidateImpact = async (
    templateId: string,
    relationId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client.post(`/${templateId}/relation/${relationId}/validateImpact`).then((response) => {
      return response.data as OutputOperationResult<ImpactBrokerResult>;
    });
  };

  public DeleteRelationApplyImpact = async (
    templateId: string,
    relationId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client.post(`/${templateId}/relation/${relationId}/applyImpact`).then((response) => {
      return response.data as OutputOperationResult<ImpactBrokerResult>;
    });
  };

  public UpdateRelationName = async (
    templateId: string,
    relationId: string,
    model: UpdateRelationNameModel
  ): Promise<OutputOperationResult<TemplateRelationNode>> => {
    return await this.client.patch(`/${templateId}/relation/${relationId}/name`, model).then((response) => {
      return response.data as OutputOperationResult<TemplateRelationNode>;
    });
    //api/template/{templateId:guid}/relation
  };

  public PostQuery = async (templateId: string, model: CreateQueryModel): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client.post(`/${templateId}/queries/`, model).then((response) => {
      return response.data as OutputOperationResult<DataQuery>;
    });
  };

  public UpdateQueryName = async (
    templateId: string,
    queryId: string,
    model: UpdateQueryModel
  ): Promise<OutputOperationResult<string>> => {
    return await this.client.patch(`/${templateId}/queries/${queryId}`, model).then((response) => {
      return response.data as OutputOperationResult<string>;
    });
  };

  public UpdateQueryIndex = async (
    templateId: string,
    queryId: string,
    model: UpdateQueryModel
  ): Promise<OutputOperationResult<string>> => {
    return await this.client.patch(`/${templateId}/queries/${queryId}/index`, model).then((response) => {
      return response.data as OutputOperationResult<string>;
    });
  };

  public UpdateQueryNumberOfRowsDisplayed = async (
    templateId: string,
    queryId: string,
    model: UpdateQueryModel
  ): Promise<OutputOperationResult<string>> => {
    return await this.client
      .patch(`/${templateId}/queries/${queryId}/numberOfRowsDisplayed`, model)
      .then((response) => {
        return response.data as OutputOperationResult<string>;
      });
  };

  public DeleteQuery = async (
    templateId: string,
    queryId: string
  ): Promise<OutputOperationResult<RemoveNodeTraversalResult>> => {
    return await this.client.post(`/${templateId}/queries/${queryId}:delete`).then((response) => {
      return response.data as OutputOperationResult<RemoveNodeTraversalResult>;
    });
  };

  public AddTraversalToQuery = async (
    templateId: string,
    queryId: string,
    model: CreateTraverseToNodeModel
  ): Promise<OutputOperationResult<AddNodeTraversalResult>> => {
    return await this.client.post(`/${templateId}/queries/${queryId}`, model).then((response) => {
      return response.data as OutputOperationResult<AddNodeTraversalResult>;
    });
  };

  //AddTraversalToQuery
  public AddTraversalToSingleValueQuery = async (
    templateId: string,
    queryId: string,
    model: CreateTraverseToNodeModel
  ): Promise<OutputOperationResult<AddNodeTraversalResult>> => {
    return await this.client.post(`/${templateId}/singlevaluequeries/${queryId}`, model).then((response) => {
      return response.data as OutputOperationResult<AddNodeTraversalResult>;
    });
  };

  public RemoveNodeTraversalFromQuery = async (
    templateId: string,
    queryId: string,
    nodeTraversalId: string
  ): Promise<OutputOperationResult<RemoveNodeTraversalResult>> => {
    return await this.client
      .post(`/${templateId}/queries/${queryId}/traversals/${nodeTraversalId}:delete`)
      .then((response) => {
        return response.data as OutputOperationResult<RemoveNodeTraversalResult>;
      });
  };

  //RemoveNodeTraversalFromQuery
  public RemoveNodeTraversalFromSingleValueQuery = async (
    templateId: string,
    queryId: string,
    nodeTraversalId: string
  ): Promise<OutputOperationResult<RemoveNodeTraversalResult>> => {
    return await this.client
      .post(`/${templateId}/singlevaluequeries/${queryId}/traversals/${nodeTraversalId}:delete`)
      .then((response) => {
        return response.data as OutputOperationResult<RemoveNodeTraversalResult>;
      });
  };

  public AddReverseTraversalToQuery = async (
    templateId: string,
    queryId: string,
    model: CreateTraverseToNodeModel
  ): Promise<OutputOperationResult<AddNodeTraversalResult>> => {
    return await this.client.post(`/${templateId}/queries/${queryId}:reverse`, model).then((response) => {
      return response.data as OutputOperationResult<AddNodeTraversalResult>;
    });
  };

  //AddReverseTraversalToQuery
  public AddReverseTraversalToSingleValueQuery = async (
    templateId: string,
    queryId: string,
    model: CreateTraverseToNodeModel
  ): Promise<OutputOperationResult<AddNodeTraversalResult>> => {
    return await this.client.post(`/${templateId}/singlevaluequeries/${queryId}:reverse`, model).then((response) => {
      return response.data as OutputOperationResult<AddNodeTraversalResult>;
    });
  };

  public AddPropertyToQueryNode = async (
    templateId: string,
    queryId: string,
    nodeId: string,
    model: AddPropertyToNodeModel
  ): Promise<OutputOperationResult<ITraverseProperty>> => {
    return await this.client
      .post(`/${templateId}/queries/${queryId}/nodes/${nodeId}/properties`, model)
      .then((response) => {
        return response.data as OutputOperationResult<ITraverseProperty>;
      });
  };

  //AddPropertyToQueryNode
  public AddPropertyToSingleValueQueryNode = async (
    templateId: string,
    queryId: string,
    nodeId: string,
    model: AddPropertyToNodeModel
  ): Promise<OutputOperationResult<ITraverseProperty>> => {
    return await this.client
      .post(`/${templateId}/singlevaluequeries/${queryId}/nodes/${nodeId}/properties`, model)
      .then((response) => {
        return response.data as OutputOperationResult<ITraverseProperty>;
      });
  };

  public RemovePropertyFromQueryNode = async (
    templateId: string,
    queryId: string,
    nodeId: string,
    model: DeletePropertyFromNodeModel
  ): Promise<OutputOperationResult<ReturnNode>> => {
    return await this.client
      .post(`/${templateId}/queries/${queryId}/nodes/${nodeId}/properties:delete`, model)
      .then((response) => {
        return response.data as OutputOperationResult<ReturnNode>;
      });
  };

  //RemovePropertyFromQueryNode
  public RemovePropertyFromSingleValueQueryQueryNode = async (
    templateId: string,
    queryId: string,
    nodeId: string,
    model: DeletePropertyFromNodeModel
  ): Promise<OutputOperationResult<ValueSourceNode>> => {
    return await this.client
      .post(`/${templateId}/singleValueQueries/${queryId}/nodes/${nodeId}/properties:delete`, model)
      .then((response) => {
        return response.data as OutputOperationResult<ValueSourceNode>;
      });
  };

  public SetTargetValueSource = async (
    templateId: string,
    queryId: string,
    targetValueSourceId: string
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(`/${templateId}/singlevaluequeries/${queryId}/target`, {
        targetValueSourceId: targetValueSourceId
      } as SetTargetValueSourceModel)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public GetQueryDependencies = async (templateId: string): Promise<OutputOperationResult<any>> => {
    return await this.client.get(`/${templateId}/dependencies`).then((response) => {
      return response.data as OutputOperationResult<any>;
    });
  };

  public AddProperty = async (
    templateId: string,
    nodeId: string,
    model: CreatePropertyModel
  ): Promise<OutputOperationResult<Property>> => {
    return await this.client.post(`/${templateId}/node/${nodeId}/property`, model).then((response) => {
      return response.data as OutputOperationResult<Property>;
    });
  };

  public AddPicklistProperty = async (
    templateId: string,
    nodeId: string,
    model: CreatePicklistPropertyModel
  ): Promise<OutputOperationResult<Property>> => {
    return await this.client.post(`/${templateId}/node/${nodeId}/property:picklist`, model).then((response) => {
      return response.data as OutputOperationResult<Property>;
    });
  };

  public AddAutoIdProperty = async (
    templateId: string,
    nodeId: string,
    model: CreateAutoIdPropertyModel
  ): Promise<OutputOperationResult<Property>> => {
    return await this.client.post(`/${templateId}/node/${nodeId}/property:auto-id`, model).then((response) => {
      return response.data as OutputOperationResult<Property>;
    });
  };

  public AddCalculatedProperty = async (
    templateId: string,
    nodeId: string,
    model: CreateCalculatedPropertyModel
  ): Promise<OutputOperationResult<CalculatedPropertyCreationResult>> => {
    return await this.client.post(`/${templateId}/node/${nodeId}/property:calculated`, model).then((response) => {
      return response.data as OutputOperationResult<CalculatedPropertyCreationResult>;
    });
  };

  public SetPropertyAsPrimary = async (
    templateId: string,
    nodeId: string,
    propertyId: string
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client.post(`/${templateId}/node/${nodeId}/property/${propertyId}:primary`).then((response) => {
      return response.data as OutputOperationResult<IHaveId>;
    });
  };

  public DeletePropertyFromTemplateNode = async (
    templateId: string,
    nodeId: string,
    propertyId: string
  ): Promise<OutputOperationResult<Template>> => {
    return await this.client.delete(`/${templateId}/node/${nodeId}/property/${propertyId}`).then((response) => {
      return response.data as OutputOperationResult<Template>;
    });
  };

  public DeletePropertyFromTemplateNodeValidateImpact = async (
    templateId: string,
    nodeId: string,
    propertyId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client
      .delete(`/${templateId}/node/${nodeId}/property/${propertyId}/validateImpact`)
      .then((response) => {
        return response.data as OutputOperationResult<ImpactBrokerResult>;
      });
  };

  public DeletePropertyFromTemplateNodeApplyImpact = async (
    templateId: string,
    nodeId: string,
    propertyId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client
      .delete(`/${templateId}/node/${nodeId}/property/${propertyId}/applyImpact`)
      .then((response) => {
        return response.data as OutputOperationResult<ImpactBrokerResult>;
      });
  };

  public GetQuery = async (templateId: string, queryId: string): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client.get(`/${templateId}/queries/${queryId}`).then((response) => {
      return response.data as OutputOperationResult<DataQuery>;
    });
  };

  // GetQuery
  public GetSingleValueQuery = async (
    templateId: string,
    queryId: string
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client.get(`/${templateId}/singlevaluequeries/${queryId}`).then((response) => {
      return response.data as OutputOperationResult<SingleValueQuery>;
    });
  };

  public AddConditionToQuery = async (
    templateId: string,
    queryId: string,
    nodeId: string,
    model: CreateConditionModel
  ): Promise<OutputOperationResult<Condition>> => {
    return await this.client.post(`/${templateId}/queries/${queryId}/nodes/${nodeId}`, model).then((response) => {
      return response.data as OutputOperationResult<Condition>;
    });
  };

  // AddConditionToQuery
  public AddConditionToSingleValueQuery = async (
    templateId: string,
    queryId: string,
    nodeId: string,
    model: CreateConditionModel
  ): Promise<OutputOperationResult<Condition>> => {
    return await this.client
      .post(`/${templateId}/singleValueQueries/${queryId}/nodes/${nodeId}`, model)
      .then((response) => {
        return response.data as OutputOperationResult<Condition>;
      });
  };

  public AddConditionToCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: AddConditionToConditionModel
  ): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client
      .post(`/${templateId}/queries/${queryId}/conditions/${conditionId}/conditions`, model)
      .then((response) => {
        return response.data as OutputOperationResult<DataQuery>;
      });
  };

  //AddConditionToCondition
  public AddConditionToSingleValueQueryCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: AddConditionToConditionModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(`/${templateId}/singleValueQueries/${queryId}/conditions/${conditionId}/conditions`, model)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public DeleteCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string
  ): Promise<OutputOperationResult<RemoveNodeTraversalResult>> => {
    return await this.client.delete(`/${templateId}/queries/${queryId}/conditions/${conditionId}`).then((response) => {
      return response.data as OutputOperationResult<RemoveNodeTraversalResult>;
    });
  };

  //DeleteCondition
  public DeleteConditionFromSingleValueQuery = async (
    templateId: string,
    queryId: string,
    conditionId: string
  ): Promise<OutputOperationResult<RemoveNodeTraversalResult>> => {
    return await this.client
      .delete(`/${templateId}/singleValueQueries/${queryId}/conditions/${conditionId}`)
      .then((response) => {
        return response.data as OutputOperationResult<RemoveNodeTraversalResult>;
      });
  };

  public AddValueSourceToCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: CreateValueSourceModel
  ): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client
      .post(`/${templateId}/queries/${queryId}/conditions/${conditionId}/valuesources`, model)
      .then((response) => {
        return response.data as OutputOperationResult<DataQuery>;
      });
  };

  // AddValueSourceToCondition
  public AddValueSourceToSingleValueQueryCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: CreateValueSourceModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(`/${templateId}/singleValueQueries/${queryId}/conditions/${conditionId}/valuesources`, model)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public UpdateStaticValueSourceValue = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: SetStaticValueSourceValueModel
  ): Promise<OutputOperationResult<{ id: string }>> => {
    return await this.client
      .patch(
        `/${templateId}/queries/${queryId}/conditions/${conditionId}/staticvaluesources/${model.targetStaticValueSourceId}`,
        model
      )
      .then((response) => {
        return response.data as OutputOperationResult<{ id: string }>;
      });
  };

  public AddPropertyValueSourceToCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: CreatePropertyValueSourceModel
  ): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client
      .post(`/${templateId}/queries/${queryId}/conditions/${conditionId}/propertyvaluesources`, model)
      .then((response) => {
        return response.data as OutputOperationResult<DataQuery>;
      });
  };

  //AddPropertyValueSourceToCondition
  public AddPropertyValueSourceToSingleValueQueryCondition = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: CreatePropertyValueSourceModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(`/${templateId}/singlevaluequeries/${queryId}/conditions/${conditionId}/propertyvaluesources`, model)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public DeleteValueSourceProperty = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    valueSourceId: string,
    model: DeleteValueSourceModel
  ): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client
      .post(
        `/${templateId}/queries/${queryId}/conditions/${conditionId}/propertyvaluesources/${valueSourceId}:delete`,
        model
      )
      .then((response) => {
        return response.data as OutputOperationResult<DataQuery>;
      });
  };

  //DeleteValueSourceProperty
  public DeleteValueSourcePropertyFromSingleValueQuery = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    valueSourceId: string,
    model: DeleteValueSourceModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(
        `/${templateId}/singlevaluequeries/${queryId}/conditions/${conditionId}/propertyvaluesources/${valueSourceId}:delete`,
        model
      )
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public UpdateComparisonConditionOperator = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: UpdateOperatorModel
  ): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client
      .patch(`/${templateId}/queries/${queryId}/conditions/${conditionId}/operator`, model)
      .then((response) => {
        return response.data as OutputOperationResult<DataQuery>;
      });
  };

  public UpdateBasicOperatorFunctionStatement = async (
    templateId: string,
    queryId: string,
    basicOperatorFunctionId: string,
    model: UpdateOperatorModel
  ): Promise<OutputOperationResult<DataQuery>> => {
    return await this.client
      .patch(`/${templateId}/singlevaluequeries/${queryId}/valuesources/${basicOperatorFunctionId}/statement`, model)
      .then((response) => {
        return response.data as OutputOperationResult<DataQuery>;
      });
  };

  //UpdateComparisonConditionOperator
  public UpdateSingleValueQueryComparisonConditionOperator = async (
    templateId: string,
    queryId: string,
    conditionId: string,
    model: UpdateOperatorModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .patch(`/${templateId}/singlevaluequeries/${queryId}/conditions/${conditionId}/operator`, model)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public AddProjectView = async (templateId: string): Promise<OutputOperationResult<ProjectView>> => {
    return await this.client.post(`/${templateId}/projectviews`).then((response) => {
      return response.data as OutputOperationResult<ProjectView>;
    });
  };

  public DeleteProjectView = async (
    templateId: string,
    projectViewId: string
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client.delete(`/${templateId}/projectviews/${projectViewId}`).then((response) => {
      return response.data as OutputOperationResult<IHaveId>;
    });
  };

  public UpdateProjectViewName = async (
    templateId: string,
    projectViewId: string,
    newName: string
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/projectviews/${projectViewId}`, { newName: newName } as UpdateProjectViewNameModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public UpdateProjectViewIndex = async (
    templateId: string,
    projectViewId: string,
    newIndex: number
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/projectviews/${projectViewId}/index`, {
        newIndex: newIndex
      } as UpdateProjectViewIndexModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public AddHeaderToProjectView = async (
    templateId: string,
    projectViewId: string
  ): Promise<OutputOperationResult<ProjectViewHeader>> => {
    return await this.client.post(`/${templateId}/projectviews/${projectViewId}/headers`).then((response) => {
      return response.data as OutputOperationResult<ProjectViewHeader>;
    });
  };

  public UpdateProjectViewHeaderIndex = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    newIndex: number
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/index`, {
        newIndex: newIndex
      } as UpdateProjectViewIndexModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public UpdateProjectViewHeaderName = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    newName: string
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}`, {
        newName: newName
      } as UpdateProjectViewNameModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public DeleteProjectViewHeader = async (
    templateId: string,
    projectViewId: string,
    headerId: string
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .delete(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}`)
      .then((response) => {
        return response.data as OutputOperationResult<IHaveId>;
      });
  };

  public AddOverviewToProjectHeader = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    overviewId: string
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .post(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/overviews/${overviewId}`)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public UpdateProjectViewHeaderOverviewIndex = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    overviewId: string,
    newIndex: number
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/overviews/${overviewId}/index`, {
        newIndex: newIndex
      } as UpdateProjectViewIndexModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public RemoveOverviewFromProjectHeader = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    overviewId: string
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .delete(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/overviews/${overviewId}`)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public AddHierarchyToProjectHeader = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    relationId: string
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .post(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/decompositions/${relationId}`)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public UpdateProjectViewHeaderHierarchyIndex = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    relationId: string,
    newIndex: number
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/decompositions/${relationId}/index`, {
        newIndex: newIndex
      } as UpdateProjectViewIndexModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public RemoveHierarchyFromProjectHeader = async (
    templateId: string,
    projectViewId: string,
    headerId: string,
    relationId: string
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .delete(`/${templateId}/projectviews/${projectViewId}/headers/${headerId}/decompositions/${relationId}`)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public AddDetailViewToProjectView = async (
    templateId: string,
    projectViewId: string,
    detailviewId: string
  ): Promise<OutputOperationResult<string[]>> => {
    return await this.client
      .post(`/${templateId}/projectviews/${projectViewId}/detailviews/${detailviewId}`)
      .then((response) => {
        return response.data as OutputOperationResult<string[]>;
      });
  };

  public RemoveDetailViewFromProjectView = async (
    templateId: string,
    projectViewId: string,
    detailviewId: string
  ): Promise<OutputOperationResult<CountResult>> => {
    return await this.client
      .delete(`/${templateId}/projectviews/${projectViewId}/detailviews/${detailviewId}`)
      .then((response) => {
        return response.data as OutputOperationResult<CountResult>;
      });
  };

  public SetTemplateReleaseNotes = async (
    templateId: string,
    releaseNotes: string
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .patch(`/${templateId}/releasenotes`, { releaseNotes: releaseNotes } as SetTemplateReleaseNotesModel)
      .then((response) => {
        return response.data as OutputOperationResult<IHaveId>;
      });
  };

  public SetTemplateReleased = async (templateId: string): Promise<TemplateReleaseRequestedEvent> => {
    return await this.client.post(`/${templateId}:released`).then((response) => {
      return response.data as TemplateReleaseRequestedEvent;
    });
  };

  public SetTemplateUnReleased = async (templateId: string): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client.post(`/${templateId}:not-released`).then((response) => {
      return response.data as OutputOperationResult<IHaveId>;
    });
  };

  public MoveRelationIndex = async (
    templateId: string,
    queryId: string,
    parentNodeId: string,
    relationId: string,
    direction: EMoveDirection
  ): Promise<OutputOperationResult<UpdateRelationIndexResult>> => {
    return await this.client
      .patch(`/${templateId}/queries/${queryId}/nodes/${parentNodeId}/relations/${relationId}:move`, {
        direction: direction
      } as MoveRelationIndexModel)
      .then((response) => {
        return response.data as OutputOperationResult<UpdateRelationIndexResult>;
      });
  };

  public CreateModule = async (
    templateId: string,
    technicalName: string,
    displayName: string,
    color: string,
    nodes?: string[],
    edges?: string[]
  ): Promise<OutputOperationResult<Module>> => {
    return await this.client
      .post(`/${templateId}/module`, {
        technicalName,
        displayName,
        color,
        nodes,
        relations: edges
      })
      .then((response) => {
        return response.data as OutputOperationResult<Module>;
      });
  };

  public DeleteModule = async (templateId: string, moduleId: string): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client.delete(`/${templateId}/module/${moduleId}`).then((response) => {
      return response.data as OutputOperationResult<BoolResult>;
    });
  };

  public DeleteModuleValidateImpact = async (
    templateId: string,
    moduleId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client.delete(`/${templateId}/module/${moduleId}/validateImpact`).then((response) => {
      return response.data as OutputOperationResult<ImpactBrokerResult>;
    });
  };

  public DeleteModuleApplyImpact = async (
    templateId: string,
    moduleId: string
  ): Promise<OutputOperationResult<ImpactBrokerResult>> => {
    return await this.client.delete(`/${templateId}/module/${moduleId}/applyImpact`).then((response) => {
      return response.data as OutputOperationResult<ImpactBrokerResult>;
    });
  };

  public ConnectNodesToModule = async (
    templateId: string,
    moduleId: string,
    nodes: string[]
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .post(`/${templateId}/module/${moduleId}/nodes/connect`, { nodes } as ConnectNodeToModuleModel)
      .then((response) => {
        return response.data as OutputOperationResult<Module>;
      });
  };

  public DisconnectNodesFromModule = async (
    templateId: string,
    moduleId: string,
    nodes: string[]
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .post(`/${templateId}/module/${moduleId}/nodes/disconnect`, { nodes } as ConnectNodeToModuleModel)
      .then((response) => {
        return response.data as OutputOperationResult<Module>;
      });
  };

  public ConnectEdgesToModule = async (
    templateId: string,
    moduleId: string,
    edges: string[]
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .post(`/${templateId}/module/${moduleId}/edges/connect`, { edges } as ConnectEdgeToModuleModel)
      .then((response) => {
        return response.data as OutputOperationResult<Module>;
      });
  };

  public DisconnectEdgesFromModule = async (
    templateId: string,
    moduleId: string,
    edges: string[]
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .post(`/${templateId}/module/${moduleId}/edges/disconnect`, { edges } as ConnectEdgeToModuleModel)
      .then((response) => {
        return response.data as OutputOperationResult<Module>;
      });
  };

  public UpdateModuleColor = async (
    templateId: string,
    moduleId: string,
    color: string
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .patch(`/${templateId}/module/${moduleId}/color`, { color } as UpdateModuleColorModel)
      .then((response) => {
        return response.data as OutputOperationResult<Module>;
      });
  };

  public AddPropertyDisplayName = async (
    templateId: string,
    moduleId: string,
    displayName: string,
    languageCode: string
  ): Promise<OutputOperationResult<IHaveId>> => {
    return await this.client
      .post(`/${templateId}/module/${moduleId}/displayname`, {
        displayName,
        languageCode
      } as CreateDisplayNameModel)
      .then((response) => {
        return response.data as OutputOperationResult<CreateDisplayNameNodeResult>;
      });
  };

  public UpdatePropertyDisplayNames = async (
    templateId: string,
    moduleId: string,
    values: Record<string, string>
  ): Promise<OutputOperationResult<BoolResult>> => {
    return await this.client
      .patch(`/${templateId}/module/${moduleId}/displayname`, { values } as UpdateNodeDisplayNamesModel)
      .then((response) => {
        return response.data as OutputOperationResult<BoolResult>;
      });
  };

  public ValidateTemplate = async (templateId: string): Promise<TemplateValidationResult[]> => {
    return await this.client.get(`/${templateId}/validate`).then((response) => {
      return response.data as TemplateValidationResult[];
    });
  };
  public FixTemplate = async (templateId: string): Promise<TemplateValidationResult[]> => {
    return await this.client.post(`/${templateId}/validate:fix`).then((response) => {
      return response.data as TemplateValidationResult[];
    });
  };

  // QueryNodeController
  public AddValueSourceToSingleValueQueryNodeTraversal = async (
    templateId: string,
    queryId: string,
    nodeTraversalId: string,
    model: AddValueSourceToNodeTraversalModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(
        `/${templateId}/singlevaluequeries/${queryId}/traversals/${nodeTraversalId}/valuesources`,
        model as AddValueSourceToNodeTraversalModel
      )
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public AddValueSourcePropertyToSingleValueQueryValueSource = async (
    templateId: string,
    queryId: string,
    valueSourceId: string,
    model: AddValueSourceToValueSourceModel
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .post(
        `/${templateId}/singlevaluequeries/${queryId}/valuesources/${valueSourceId}/valuesources`,
        model as AddValueSourceToValueSourceModel
      )
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public DeleteValueSourceFromSingleValueQueryNode = async (
    templateId: string,
    queryId: string,
    nodeTraversalId: string,
    valueSourceId: string
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .delete(
        `/${templateId}/singlevaluequeries/${queryId}/traversals/${nodeTraversalId}/valuesources/${valueSourceId}`
      )
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public SetCountFunctionTargetNodeTraversal = async (
    templateId: string,
    queryId: string,
    valueSourceId: string,
    targetNodeTraversal: string
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .patch(`/${templateId}/singlevaluequeries/${queryId}/valuesources/${valueSourceId}/target`, {
        targetNodeTraversalId: targetNodeTraversal
      } as SetTargetNodeTraversalModel)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public SetAggregatingFunctionTargetNodeTraversal = async (
    templateId: string,
    queryId: string,
    valueSourceId: string,
    targetNodeTraversal: string,
    targetPropertyId: string
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .patch(`/${templateId}/singlevaluequeries/${queryId}/valuesources/${valueSourceId}/aggregate`, {
        targetNodeTraversalId: targetNodeTraversal,
        targetPropertyId: targetPropertyId
      } as SetAggregatingValueSourceTargetPropertyModel)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  public SetAggregatingFunctionAggregationType = async (
    templateId: string,
    queryId: string,
    valueSourceId: string,
    aggregatingValueSourceType: EAggregatingValueSourceType
  ): Promise<OutputOperationResult<SingleValueQuery>> => {
    return await this.client
      .patch(`/${templateId}/singlevaluequeries/${queryId}/valuesources/${valueSourceId}/aggregate:type`, {
        aggregatingValueSourceType: aggregatingValueSourceType
      } as SetAggregatingValueSourceTypeModel)
      .then((response) => {
        return response.data as OutputOperationResult<SingleValueQuery>;
      });
  };

  // Reports
  public CreateReport = async (templateId: string, model: CreateReportDto) => {
    return await this.client.post(`/${templateId}/reports`, model).then((response) => {
      return response.data as OutputOperationResult<IReportOption>;
    });
  };

  public DeleteReport = async (templateId: string, reportId: string) => {
    return await this.client.delete(`/${templateId}/reports/${reportId}`).then((response) => {
      return response.data as OutputOperationResult<IHaveId>;
    });
  };

  public UpdateReport = async (templateId: string, reportId: string, model: UpdateReportDto) => {
    return await this.client.patch(`/${templateId}/reports/${reportId}`, model).then((response) => {
      return response.data as OutputOperationResult<IReportOption>;
    });
  };

  public UploadReportTemplate = async (templateId: string, reportId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return await this.client
      .post(`/${templateId}/reports/${reportId}/template`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((response) => {
        return response.data as OutputOperationResult<IReportOption>;
      });
  };

  // personal page
  public AddQueryToPersonalPages = async (templateId: string, queryId: string) => {
    return await this.client
      .post<OutputOperationResult<void>>(`/${templateId}/personalqueries/${queryId}`)
      .then((response) => response.data);
  };

  public RemoveQueryFromPersonalPages = async (templateId: string, queryId: string) => {
    return await this.client
      .delete<OutputOperationResult<void>>(`/${templateId}/personalqueries/${queryId}`)
      .then((response) => response.data);
  };

  // file attachements
  public SetFileAttachmentOptions = async (templateId: string, nodeId: string, settings: string[]) => {
    return await this.client.post(`/${templateId}/node/${nodeId}:file-attachments`, settings).then((response) => {
      return response.data as OutputOperationResult<Node_>;
    });
  };
}

export interface UpdateProjectViewNameModel {
  newName: string;
}

export interface UpdateProjectViewIndexModel {
  newIndex: number;
}

export interface BoolResult {
  value: boolean;
}

export interface QueryModel {
  technicalName: string;
  id: string;
}

export interface UpdateTemplateName {
  newName: string;
}

export interface CreateNodeModel {
  displayName: string;
  technicalName: string;
  icon: string;
  x: number;
  y: number;
  z: number;
}

export interface CreateTemplateModel {
  technicalName: string;
  defaultLanguage: string;
  supportedLanguages: string[];
}

export interface UpdateNodeCoordinatesModel {
  x: number;
  y: number;
  z: number;
}

export interface UpdateNodeTechnicalNameModel {
  newName: string;
  icon: string;
  displayNames: DisplayName[];
}

export interface CreateTemplateRelationModel {
  startNodeId: string;
  endNodeId: string;

  startToEndBehaviour: ERelationBehaviour;

  startToEndCardinality: Cardinality;
  endToStartCardinality: Cardinality;

  technicalName: string;
  displayName: string;
}

export interface Cardinality {
  minimum: number | null;
  maximum: number | null;
}

export interface CreateQueryModel {
  startNodeId: string;
  isOverview: boolean;
  name: string;
  index: number;
  numberOfRowsDisplayed: number;
}

export interface UpdateQueryModel {
  newName: string;
  newIndex: number;
  newNumberOfRowsDisplayed: number;
}

export enum ERelationBehaviour {
  Select = 0,
  Create = 2
}

export interface CreateTraverseToNodeModel {
  startnodeid: string;
  relationid: string;
}

export interface AddNodeTraversalResult {
  newNodeTraversal: ITraverseNode;
  newRelationTraversal: ITraverseRelation;
}

export interface CreatePropertyModel {
  technicalName: string;
  displayName: string;

  index: number;
  pinned: boolean;

  group?: string;

  propertyType: EPropertyTypes;
}

export interface TemplateNodeProperty {
  id: string;
  technicalName: string;

  index: number;
  pinned: boolean;

  group?: string;

  dataType: EPropertyTypes;
}

export interface AddPropertyToNodeModel {
  targetPropertyId: string;
}

export interface DeletePropertyFromNodeModel {
  targetPropertyId: string;
}

export interface UpdateTemplateRelationBehaviorModel {
  behaviour: ERelationBehaviour;
  startToEndCardinality: Cardinality;
  endToStartCardinality: Cardinality;
}

export interface UpdateRelationNameModel {
  technicalName: string;
  displayNames: DisplayName[];
  reverseDisplayNames: DisplayName[];
}

export interface TemplateRelationNode {
  id: string;
  technicalName: string;
  behaviour: ERelationBehaviour;
  startToEndMinimumNumberOfRelations: number;
  startToEndMaximumNumberOfRelations: number;
  endToStartMinimumNumberOfRelations: number;
  endToStartMaximumNumberOfRelations: number;
  displayNames: DisplayName[];
  reverseDisplayNames: DisplayName[];
}

export interface UpdateNodeNameResult {
  id: string;
  technicalName: string;
  icon: string;
}

export interface RemoveNodeTraversalResult {
  deletedNodeIds: string[];
  deletedRelationTraversalIds: string[];
  deletedValueSourceIds: string[];
}

export interface CreateConditionModel {
  conditionType: EConditionType;
}

export interface AddConditionToConditionModel {
  conditionType: EConditionType;
  location: ECreateValueSourceLocation;
}

export enum EConditionType {
  Exists = 1,
  Not = 2,
  And = 3,
  Or = 4,
  Comparison = 5
}

export interface CreateValueSourceModel {
  valueSourceLocation: ECreateValueSourceLocation;
  valueSourceType: EValueSourceType;
}

export interface DeleteValueSourceModel {
  valueSourceLocation: ECreateValueSourceLocation;
}

export enum ECreateValueSourceLocation {
  None = 0,
  Left = 1,
  Right = 2
}

export enum EValueSourceType {
  MathOperator = 1,
  Static = 2,
  Property = 3,
  Count = 4,
  Aggregation = 5,
  BasicOperator = 6
}

export enum ECalculatedPropertyType {
  MathOperation = 0,
  Count = 1,
  AverageDuration = 2,
  AverageNumber = 3,
  Min = 4,
  Max = 5,
  PercentileCont = 6,
  PercentileDisc = 7,
  StandardDeviation = 8,
  StandardDeviationGroup = 9,
  SumNumerical = 10,
  SumDuration = 11,
  BasicOperator = 12
}

export interface CreatePropertyValueSourceModel {
  nodeTraversalId: string;
  targetPropertyId: string;
  valueSourceLocation: ECreateValueSourceLocation;
  valueSourceType: EValueSourceType;
}

export interface UpdateOperatorModel {
  newOperator: string;
}

export interface HyperRelationConversionResult {
  hyperRelation: HyperRelation;
  partOneRelation: Relation;
  hyperNode: Node_; // part two will be part of hypernode
  /**TODO: the data from the relation deletion should be included!     */
}

export interface CreatePicklistPropertyModel extends CreatePropertyModel {
  options: CreatePicklistOptionModel[];
}

export interface CreatePicklistOptionModel {
  index: number;
  instruction: string;
  isDefault: boolean;
  value: number;
  name: string;
}

export interface SetStaticValueSourceValueModel {
  targetStaticValueSourceId: string;
  valueSourceLocation: ECreateValueSourceLocation;
  value: any;
  propertyType: EPropertyTypes;
}

export interface CreateAutoIdPropertyModel extends CreatePropertyModel {
  numberOfDigits: number;
  prefix: string;
}

export interface CreateCalculatedPropertyModel extends CreatePropertyModel {
  calculatedPropertyType: ECalculatedPropertyType;
}

export interface CreateTemplateBranchModel {
  branchTechnicalName: string;
}

export interface SetTemplateReleaseNotesModel {
  releaseNotes: string;
}

export interface UpdateRelationIndexModel {
  parentNodeTraversalId: string;
  relationTraversalToMoveId: string;
  direction: EMoveDirection;
}

export interface UpdateRelationIndexResult {
  newRelationIndices: { [key: string]: number };
}

export enum EMoveDirection {
  left = 0,
  right = 1
}

export interface MoveRelationIndexModel {
  direction: EMoveDirection;
}

export interface CreateModuleModel {
  technicalName: string;
  displayName: string;
  relations: string[];
  nodes: string[];
  color: string;
}

export interface ConnectNodeToModuleModel {
  nodes: string[];
}

export interface ConnectEdgeToModuleModel {
  edges: string[];
}

export interface UpdateModuleColorModel {
  color: string;
}

export interface CreateDisplayNameModel {
  displayName: string;
  languageCode: string;
}

export interface UpdateNodeDisplayNamesModel {
  values: Record<string, string>;
}

export interface CreateDisplayNameNodeResult {
  id: string;
  technicalName: string;
}

export interface TemplateValidationResult {
  validationType: string;
  canFix: boolean;
  fixMessage: string;
  validationFailedMessage: string;
  failedItems: FailedItem[];
}

export interface FailedItem {
  message: string;
  itemToActOn: string;
}

export interface AddValueSourceToNodeTraversalModel {
  valueSourceType: EValueSourceType;
}

export interface AddValueSourceToValueSourceModel {
  valueSourceLocation: ECreateValueSourceLocation;
  targetPropertyId: string;
  targetNodeTraversalId: string;
}

export interface SetTargetValueSourceModel {
  targetValueSourceId: string;
}

export interface SetTargetNodeTraversalModel {
  targetNodeTraversalId: string;
}

export interface SetAggregatingValueSourceTargetPropertyModel {
  targetNodeTraversalId: string;
  targetPropertyId: string;
}

export interface SetAggregatingValueSourceTypeModel {
  aggregatingValueSourceType: EAggregatingValueSourceType;
}

export interface CalculatedPropertyCreationResult {
  createdProperty: Property;
  singleValueQuery: SingleValueQuery;
}

export interface TemplateReleaseRequestedEvent extends IDataEventBase {}

// Report DTO's
export interface CreateReportDto {
  reportName: string | null;
  connectedOverviewQueries: string[];
}

export interface UpdateReportDto {
  reportName: string | null;
  connectedOverviewQueries: string[];
}
